import * as React from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Search, Edit2, Trash2, History, MessageCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { RequireRole } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

interface Customer {
  id: string;
  full_name: string;
  cpf: string;
  phone: string;
  address: string;
  credit_limit: number | null;
  outstanding_balance: number;
  loyalty_points: number;
  redeemed_points: number;
  status: 'ativo' | 'inativo';
  created_at: string;
  updated_at: string;
}

interface CustomerFormData {
  full_name: string;
  cpf: string;
  phone: string;
  address_street: string;
  address_number: string;
  address_neighborhood: string;
  address_reference: string;
  credit_limit: string;
  unlimited_credit: boolean;
  person_type: 'pf' | 'pj';
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Purchase {
  id: string;
  numero: number;
  total: number;
  data_venda: string;
  items: Array<{ product_name: string; quantity: number; unit_price: number; subtotal: number }>;
  payments: Array<{ method: string; amount: number }>;
}

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatCPF(cpf: string | null): string {
  if (!cpf) return '-';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  return cpf;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  credito: 'Crédito',
  debito: 'Débito',
  fiado: 'Fiado',
};

function CustomersPageContent() {
  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [pagination, setPagination] = React.useState<Pagination>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingCustomer, setEditingCustomer] = React.useState<Customer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<Customer | null>(null);
  const [formData, setFormData] = React.useState<CustomerFormData>({
    full_name: '',
    cpf: '',
    phone: '',
    address_street: '',
    address_number: '',
    address_neighborhood: '',
    address_reference: '',
    credit_limit: '',
    unlimited_credit: true,
    person_type: 'pf',
  });
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof CustomerFormData, string>>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [historyModal, setHistoryModal] = React.useState(false);
  const [historyCustomer, setHistoryCustomer] = React.useState<Customer | null>(null);
  const [historyData, setHistoryData] = React.useState<{ debts: Array<{ id: string; amount: number; due_date: string; status: string; created_at: string }>; summary: { total_debt: number; total_paid: number } } | null>(null);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  // Purchase detail states
  const [detailCustomer, setDetailCustomer] = React.useState<Customer | null>(null);
  const [purchases, setPurchases] = React.useState<Purchase[]>([]);
  const [purchasesLoading, setPurchasesLoading] = React.useState(false);
  const [purchaseSummary, setPurchaseSummary] = React.useState<{ total_purchases: number; total_spent: number } | null>(null);
  const [dateFilter, setDateFilter] = React.useState({ start: '', end: '' });
  const [expandedPurchase, setExpandedPurchase] = React.useState<string | null>(null);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCustomers = React.useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);

      const response = await apiFetch(`/api/customers?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.data);
        setPagination(data.pagination);
      }
    } catch {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  React.useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  function openCreateModal() {
    setEditingCustomer(null);
    setFormData({
      full_name: '',
      cpf: '',
      phone: '',
      address_street: '',
      address_number: '',
      address_neighborhood: '',
      address_reference: '',
      credit_limit: '',
      unlimited_credit: true,
      person_type: 'pf',
    });
    setFormErrors({});
    setModalOpen(true);
  }

  function openEditModal(customer: Customer) {
    setEditingCustomer(customer);
    const addressParts = (customer.address || '').split(',').map(s => s.trim());
    setFormData({
      full_name: customer.full_name,
      cpf: customer.cpf,
      phone: customer.phone,
      address_street: addressParts[0] || '',
      address_number: addressParts[1] || '',
      address_neighborhood: addressParts[2] || '',
      address_reference: addressParts[3] || '',
      credit_limit: customer.credit_limit !== null ? String(customer.credit_limit) : '',
      unlimited_credit: customer.credit_limit === null,
      person_type: (customer.cpf || '').replace(/\D/g, '').length > 11 ? 'pj' : 'pf',
    });
    setFormErrors({});
    setModalOpen(true);
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof CustomerFormData, string>> = {};
    if (!formData.full_name.trim()) errors.full_name = 'Nome é obrigatório';
    
    if (!formData.unlimited_credit && (!formData.credit_limit || Number(formData.credit_limit) < 0)) {
      errors.credit_limit = 'Limite deve ser >= 0';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const address = [formData.address_street, formData.address_number, formData.address_neighborhood, formData.address_reference].filter(Boolean).join(', ');
      const body = {
        full_name: formData.full_name,
        cpf: formData.cpf || null,
        phone: formData.phone || null,
        address,
        credit_limit: formData.unlimited_credit ? null : Number(formData.credit_limit),
      };

      if (editingCustomer) {
        const response = await apiFetch(`/api/customers/${editingCustomer.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao atualizar');
        }
        toast.success('Cliente atualizado com sucesso');
      } else {
        const response = await apiFetch('/api/customers', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao criar');
        }
        toast.success('Cliente cadastrado com sucesso');
      }

      setModalOpen(false);
      fetchCustomers(pagination.page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function openHistory(customer: Customer) {
    setHistoryCustomer(customer);
    setHistoryModal(true);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const response = await apiFetch(`/api/credit/customer/${customer.id}`);
      if (response.ok) {
        const data = await response.json();
        setHistoryData({
          debts: data.debts || [],
          summary: {
            total_debt: data.total_debt ?? Number(customer.outstanding_balance),
            total_paid: data.total_paid ?? 0,
          },
        });
      } else {
        setHistoryData({ debts: [], summary: { total_debt: Number(customer.outstanding_balance), total_paid: 0 } });
      }
    } catch {
      toast.error('Erro ao carregar histórico');
      setHistoryData({ debts: [], summary: { total_debt: Number(customer.outstanding_balance), total_paid: 0 } });
    } finally {
      setHistoryLoading(false);
    }
  }

  async function openDetail(customer: Customer) {
    setDetailCustomer(customer);
    setPurchases([]);
    setPurchaseSummary(null);
    setDateFilter({ start: '', end: '' });
    setExpandedPurchase(null);
    await fetchPurchases(customer.id, '', '');
  }

  async function fetchPurchases(customerId: string, startDate: string, endDate: string) {
    setPurchasesLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      params.set('limit', '50');

      const response = await apiFetch(`/api/customers/${customerId}/purchases?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setPurchases(data.purchases || []);
        setPurchaseSummary(data.summary || null);
      }
    } catch {
      toast.error('Erro ao carregar compras');
    } finally {
      setPurchasesLoading(false);
    }
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    try {
      const response = await apiFetch(`/api/customers/${deleteConfirm.id}`, { method: 'DELETE' });
      if (!response.ok && response.status !== 204) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao excluir');
      }
      toast.success('Cliente excluído com sucesso');
      setDeleteConfirm(null);
      fetchCustomers(pagination.page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    }
  }

  function confirmDelete(customer: Customer) {
    if (Number(customer.outstanding_balance) > 0) {
      toast.error('Não é possível excluir cliente com saldo devedor pendente');
      return;
    }
    setDeleteConfirm(customer);
  }

  const columns: Column<Customer>[] = [
    {
      key: 'full_name',
      header: 'Nome',
      sortable: true,
      render: (row) => (
        <button onClick={() => openDetail(row)} className="font-medium text-foreground hover:text-primary transition-colors text-left">
          {row.full_name}
        </button>
      ),
    },
    {
      key: 'cpf',
      header: 'CPF',
      render: (row) => <span className="font-mono text-xs">{formatCPF(row.cpf)}</span>,
    },
    {
      key: 'phone',
      header: 'Telefone',
    },
    {
      key: 'credit_limit',
      header: 'Limite',
      render: (row) => (
        <span className="text-muted-foreground">
          {row.credit_limit === null ? 'Ilimitado' : formatBRL(Number(row.credit_limit))}
        </span>
      ),
    },
    {
      key: 'available',
      header: 'Disponível',
      render: (row) => {
        if (row.credit_limit === null) return <span className="text-muted-foreground">Ilimitado</span>;
        const available = Number(row.credit_limit) - Number(row.outstanding_balance);
        return <span className={available <= 0 ? 'text-destructive' : 'text-primary'}>{formatBRL(available)}</span>;
      },
    },
    {
      key: 'outstanding_balance',
      header: 'Saldo Devedor',
      render: (row) => (
        <span className={Number(row.outstanding_balance) > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}>
          {formatBRL(Number(row.outstanding_balance))}
        </span>
      ),
    },
    {
      key: 'loyalty_points',
      header: 'Pontos',
      render: (row) => <span className="text-amber-400 font-medium">{row.loyalty_points}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.status === 'ativo' ? 'success' : 'destructive'} dot>
          {row.status === 'ativo' ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-40',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              const phone = (row.phone || '').replace(/\D/g, '');
              const fullPhone = phone.length <= 11 ? `55${phone}` : phone;
              const msg = encodeURIComponent(`Olá ${row.full_name.split(' ')[0]}, informamos que você possui um saldo de ${formatBRL(Number(row.outstanding_balance))} pendente. Por favor, regularize seu débito. Obrigado!`);
              window.open(`https://wa.me/${fullPhone}?text=${msg}`, '_blank');
            }}
            disabled={Number(row.outstanding_balance) <= 0}
            className={`rounded-md p-1.5 transition-colors ${Number(row.outstanding_balance) > 0 ? 'text-green-500 hover:bg-green-500/10' : 'text-muted-foreground/30 cursor-not-allowed'}`}
            title="Cobrar via WhatsApp"
          >
            <MessageCircle className="h-4 w-4" />
          </button>
          <button
            onClick={() => openHistory(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={`Histórico de ${row.full_name}`}
            title="Ver Histórico"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={`Editar ${row.full_name}`}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => confirmDelete(row)}
            disabled={Number(row.outstanding_balance) > 0}
            className={`rounded-md p-1.5 transition-colors ${
              Number(row.outstanding_balance) > 0
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
            }`}
            aria-label={`Excluir ${row.full_name}`}
            title={Number(row.outstanding_balance) > 0 ? 'Cliente com saldo devedor não pode ser excluído' : undefined}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Clientes
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gerencie o cadastro de clientes e limites de crédito
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4" />
          Novo Cliente
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          <option value="">Todos status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={customers}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhum cliente encontrado"
          />
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {pagination.total} cliente(s) encontrado(s)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchCustomers(pagination.page - 1)}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {pagination.page} de {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchCustomers(pagination.page + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
        description={editingCustomer ? 'Atualize as informações do cliente' : 'Preencha os dados para cadastrar um novo cliente'}
        className="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Person type toggle */}
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="person_type"
                checked={formData.person_type === 'pf'}
                onChange={() => setFormData(p => ({ ...p, person_type: 'pf', cpf: '' }))}
                className="h-4 w-4 accent-primary"
              />
              Pessoa Física
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="person_type"
                checked={formData.person_type === 'pj'}
                onChange={() => setFormData(p => ({ ...p, person_type: 'pj', cpf: '' }))}
                className="h-4 w-4 accent-primary"
              />
              Pessoa Jurídica
            </label>
          </div>

          <div>
            <label htmlFor="customer-name" className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Nome Completo
            </label>
            <input
              id="customer-name"
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData((p) => ({ ...p, full_name: e.target.value }))}
              className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.full_name ? 'border-destructive' : 'border-input'}`}
            />
            {formErrors.full_name && <p className="mt-1 text-xs text-destructive">{formErrors.full_name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="customer-cpf" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {formData.person_type === 'pj' ? 'CNPJ' : 'CPF'}
              </label>
              <input
                id="customer-cpf"
                type="text"
                value={formData.cpf}
                onChange={(e) => {
                  const maxLen = formData.person_type === 'pj' ? 14 : 11;
                  const digits = e.target.value.replace(/\D/g, '').slice(0, maxLen);
                  let masked = digits;
                  if (formData.person_type === 'pf') {
                    masked = digits
                      .replace(/^(\d{3})(\d)/, '$1.$2')
                      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
                      .replace(/\.(\d{3})(\d)/, '.$1-$2');
                  } else {
                    masked = digits
                      .replace(/^(\d{2})(\d)/, '$1.$2')
                      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                      .replace(/\.(\d{3})(\d)/, '.$1/$2')
                      .replace(/(\d{4})(\d)/, '$1-$2');
                  }
                  setFormData((p) => ({ ...p, cpf: masked }));
                }}
                placeholder={formData.person_type === 'pj' ? '00.000.000/0000-00' : '000.000.000-00'}
                className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.cpf ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.cpf && <p className="mt-1 text-xs text-destructive">{formErrors.cpf}</p>}
            </div>
            <div>
              <label htmlFor="customer-phone" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Telefone
              </label>
              <input
                id="customer-phone"
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                placeholder="(00) 00000-0000"
                className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.phone ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.phone && <p className="mt-1 text-xs text-destructive">{formErrors.phone}</p>}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Endereço
            </label>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.address_street}
                  onChange={(e) => setFormData((p) => ({ ...p, address_street: e.target.value }))}
                  placeholder="Rua / Avenida"
                  className={`flex h-10 w-full rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.address_street ? 'border-destructive' : 'border-input'}`}
                />
              </div>
              <div>
                <input
                  type="text"
                  value={formData.address_number}
                  onChange={(e) => setFormData((p) => ({ ...p, address_number: e.target.value }))}
                  placeholder="Número"
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                type="text"
                value={formData.address_neighborhood}
                onChange={(e) => setFormData((p) => ({ ...p, address_neighborhood: e.target.value }))}
                placeholder="Bairro"
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
              <input
                type="text"
                value={formData.address_reference}
                onChange={(e) => setFormData((p) => ({ ...p, address_reference: e.target.value }))}
                placeholder="Ponto de referência"
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            {formErrors.address_street && <p className="mt-1 text-xs text-destructive">{formErrors.address_street}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Limite de Crédito
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.unlimited_credit}
                  onChange={(e) => setFormData((p) => ({ ...p, unlimited_credit: e.target.checked, credit_limit: '' }))}
                  className="h-4 w-4 rounded border-input"
                />
                Ilimitado
              </label>
              {!formData.unlimited_credit && (
                <input
                  id="customer-credit-limit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.credit_limit}
                  onChange={(e) => setFormData((p) => ({ ...p, credit_limit: e.target.value }))}
                  placeholder="Valor do limite (R$)"
                  className={`flex h-10 flex-1 rounded-lg border bg-background px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.credit_limit ? 'border-destructive' : 'border-input'}`}
                />
              )}
            </div>
            {formErrors.credit_limit && <p className="mt-1 text-xs text-destructive">{formErrors.credit_limit}</p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Salvando...' : editingCustomer ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* History Modal */}
      <Modal
        open={historyModal}
        onClose={() => setHistoryModal(false)}
        title={historyCustomer ? `Histórico - ${historyCustomer.full_name}` : 'Histórico'}
        description="Resumo de crédito e débitos do cliente"
        className="max-w-lg"
      >
        {historyLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : historyData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Total em Débito</p>
                <p className="text-lg font-bold text-destructive">{formatBRL(historyData.summary.total_debt)}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Total Pago</p>
                <p className="text-lg font-bold text-green-500">{formatBRL(historyData.summary.total_paid)}</p>
              </div>
            </div>

            {historyData.debts.length > 0 ? (
              <div>
                <h4 className="mb-2 text-sm font-medium text-foreground">Débitos</h4>
                <div className="max-h-60 space-y-2 overflow-y-auto">
                  {historyData.debts.map((debt) => (
                    <div key={debt.id} className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2 text-sm">
                      <div>
                        <p className="font-medium text-foreground">{formatBRL(debt.amount)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(debt.created_at).toLocaleDateString('pt-BR')}
                          {debt.due_date && ` • Venc: ${new Date(debt.due_date).toLocaleDateString('pt-BR')}`}
                        </p>
                      </div>
                      <Badge variant={debt.status === 'pago' ? 'success' : debt.status === 'vencido' ? 'destructive' : 'default'} dot>
                        {debt.status === 'pago' ? 'Pago' : debt.status === 'vencido' ? 'Vencido' : 'Pendente'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-4">Nenhum débito registrado</p>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setHistoryModal(false)}>
                Fechar
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Purchase Detail Modal */}
      <Modal
        open={!!detailCustomer}
        onClose={() => setDetailCustomer(null)}
        title={`Cliente: ${detailCustomer?.full_name ?? ''}`}
        className="max-w-2xl"
      >
        {detailCustomer && (
          <div className="space-y-4">
            {/* Customer info cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                <p className="text-sm font-bold text-destructive">{formatBRL(Number(detailCustomer.outstanding_balance))}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Pontos</p>
                <p className="text-sm font-bold text-amber-400">{detailCustomer.loyalty_points}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Total Compras</p>
                <p className="text-sm font-bold text-foreground">{purchaseSummary?.total_purchases ?? '—'}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Total Gasto</p>
                <p className="text-sm font-bold text-primary">{purchaseSummary ? formatBRL(purchaseSummary.total_spent) : '—'}</p>
              </div>
            </div>

            {/* Customer details */}
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>CPF/CNPJ: {formatCPF(detailCustomer.cpf)}</span>
              <span>Tel: {detailCustomer.phone}</span>
            </div>

            {/* Date filter */}
            <div className="flex items-end gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Data Início</label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter(f => ({ ...f, start: e.target.value }))}
                  className="h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Data Fim</label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter(f => ({ ...f, end: e.target.value }))}
                  className="h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchPurchases(detailCustomer.id, dateFilter.start, dateFilter.end)}
              >
                Filtrar
              </Button>
            </div>

            {/* Purchase list */}
            {purchasesLoading ? (
              <div className="flex h-24 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : purchases.length > 0 ? (
              <div className="max-h-80 space-y-2 overflow-y-auto">
                {purchases.map((purchase) => (
                  <div key={purchase.id} className="rounded-lg border border-border/50">
                    <button
                      onClick={() => setExpandedPurchase(expandedPurchase === purchase.id ? null : purchase.id)}
                      className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-accent/50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {expandedPurchase === purchase.id ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Venda #{purchase.numero}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(purchase.data_venda).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-foreground">{formatBRL(Number(purchase.total))}</p>
                        {purchase.payments.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {purchase.payments.map(p => PAYMENT_METHOD_LABELS[p.method] || p.method).join(', ')}
                          </p>
                        )}
                      </div>
                    </button>
                    {expandedPurchase === purchase.id && (
                      <div className="border-t border-border/50 px-3 py-2 space-y-1">
                        {purchase.items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                              {item.quantity}x {item.product_name}
                            </span>
                            <span className="text-foreground">{formatBRL(item.subtotal)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-6">Nenhuma compra encontrada</p>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setDetailCustomer(null)}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <Trash2 className="h-6 w-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Excluir cliente?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              O cliente &ldquo;{deleteConfirm.full_name}&rdquo; será removido. Esta ação não pode ser desfeita.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 rounded-xl bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CustomersPage() {
  return (
    <RequireRole roles={['gerente']}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CustomersPageContent />
      </motion.div>
    </RequireRole>
  );
}
